import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { API as API_BASE } from '../../lib/api';
import { toast } from 'sonner';
import { Sparkles, Eye, EyeOff, Mail, Lock, LogIn, X, AlertTriangle, RefreshCw } from 'lucide-react';
import axios from 'axios';
import MfaCodeInput from '../../components/auth/MfaCodeInput';
import MfaEnrollment from '../../components/auth/MfaEnrollment';
import { parseMfaError, EXPIRED_SESSION_CODES, RECOVERY_CODE_USED_MESSAGE } from '../../lib/mfaErrors';

// MfaEnrollment posts relative paths ('/auth/mfa/setup'), so it MUST get an
// instance scoped to the API base — bare axios would hit the frontend origin
// and get index.html back. (The component serves both the pending-token login
// path and the Settings full-session path; see its header comment.)
const mfaApi = axios.create({ baseURL: API_BASE });

const SSO_ERROR_MESSAGES = {
    invalid_state: 'SSO session expired. Please try again.',
    missing_code: 'SSO authorization was cancelled.',
    token_exchange_failed: 'Could not complete SSO sign-in. Please try again.',
    no_access_token: 'SSO provider did not return a valid token.',
    user_fetch_failed: 'Could not retrieve your profile from the SSO provider.',
    no_email: 'No email address was returned from SSO.',
    no_account: 'No account found for your email address. Please sign up first or contact your admin.',
    no_matching_org: 'No account found for your email address. Please sign up first or contact your admin.',
    network_error: 'Network error during SSO. Please try again.',
    internal_error: 'An unexpected error occurred during SSO. Please try again.',
};

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);
    const [ssoAlert, setSsoAlert] = useState(null);
    const [emailNotVerified, setEmailNotVerified] = useState(false);
    const [resending, setResending] = useState(false);
    const { user, loading: authLoading, login, adoptSession, getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();

    // MFA step state (plan §8.1 / §12.1). The pending token lives ONLY in React
    // state — never localStorage (D5) — so a refresh means starting over.
    const [mfaStep, setMfaStep] = useState(null); // 'code' | 'enroll'
    const [mfaToken, setMfaToken] = useState(null);
    const [mfaEmail, setMfaEmail] = useState('');
    const [mfaCode, setMfaCode] = useState('');
    const [mfaRecoveryMode, setMfaRecoveryMode] = useState(false);
    const [mfaError, setMfaError] = useState(null);
    const [mfaVerifying, setMfaVerifying] = useState(false);
    // Issue #564: opt-in device trust. Unchecked by default; acted on only
    // AFTER /auth/mfa/verify succeeds (never sent with the verify request).
    const [trustDevice, setTrustDevice] = useState(false);

    useEffect(() => {
        if (!authLoading && user) {
            const dest = location.state?.from?.pathname || '/dashboard';
            navigate(dest, { replace: true });
        }
    }, [authLoading, user, location, navigate]);

    useEffect(() => {
        const ssoError = searchParams.get('sso_error');
        if (ssoError) {
            setSsoAlert(SSO_ERROR_MESSAGES[ssoError] || 'SSO sign-in failed. Please try again.');
            searchParams.delete('sso_error');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    // SSO / Google hand-off (plan §8.4): the backend set an HttpOnly
    // `mfa_pending` cookie and redirected here with ?mfa=1. Ask the server to
    // re-mint the pending token in the body so the client flow is identical to
    // the password-login MFA step.
    useEffect(() => {
        if (searchParams.get('mfa') !== '1') return;
        let cancelled = false;
        (async () => {
            try {
                const res = await axios.get(`${API_BASE}/auth/mfa/pending`, { withCredentials: true });
                if (cancelled) return;
                setMfaToken(res.data.mfa_token);
                setMfaStep(res.data.state === 'mfa_enrollment_required' ? 'enroll' : 'code');
                searchParams.delete('mfa');
                setSearchParams(searchParams, { replace: true });
            } catch (err) {
                if (cancelled) return;
                // audit F04: mfaStep is still null here and the credentials view
                // renders no mfaError element — route the failure through the
                // ssoAlert banner, which IS rendered on the credentials view.
                setSsoAlert(err.response?.data?.detail || 'Your sign-in session expired. Please sign in again.');
                searchParams.delete('mfa');
                setSearchParams(searchParams, { replace: true });
            }
        })();
        return () => { cancelled = true; };
    }, [searchParams, setSearchParams]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email || !password) return toast.error('Please fill in all fields');
        setLoading(true);
        setEmailNotVerified(false);
        try {
            const data = await login(email, password);
            // CASA 3.3.1 / plan §8.1: the login response may be an MFA challenge
            // or a forced-enrollment challenge rather than a full session.
            if (data?.password_change_required) {
                navigate('/change-password', {
                    replace: true,
                    state: { from: location.state?.from || { pathname: '/dashboard' } },
                });
                return;
            }
            if (data?.mfa_required || data?.mfa_enrollment_required) {
                setMfaEmail(email);
                setMfaToken(data.mfa_token);
                setMfaStep(data.mfa_enrollment_required ? 'enroll' : 'code');
                setMfaError(null);
                setMfaCode('');
                setMfaRecoveryMode(false);
                return;
            }
            // Full session issued — navigate as before.
            navigate('/dashboard');
        } catch (err) {
            const detail = err.response?.data?.detail || '';
            if (err.response?.status === 403 && detail.toLowerCase().includes('verify your email')) {
                setEmailNotVerified(true);
            } else {
                toast.error(detail || 'Login failed');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleMfaVerify = async (submitted) => {
        const theCode = (submitted ?? mfaCode).trim();
        if (!theCode || !mfaToken) return;
        setMfaVerifying(true);
        setMfaError(null);
        try {
            const res = await axios.post(`${API_BASE}/auth/mfa/verify`, { mfa_token: mfaToken, code: theCode });
            const { warning } = res.data;
            // Adopt the full session through AuthContext so ProtectedRoute sees
            // the user immediately — a raw localStorage write used to leave
            // user=null and flash the ReconnectingScreen until a health-poll
            // reload re-ran checkAuth (issue #557).
            adoptSession(res.data);
            // Issue #564: trust issuance runs AFTER adoptSession so the fresh
            // access token from this very sign-in authenticates the POST (the
            // Bearer idiom every other authenticated call uses). It originally
            // ran BEFORE adoptSession relying on the mfa/verify response
            // setting a session cookie — but that handler returns the session
            // payload as a plain JSON body and never sets one, so the request
            // went out with no credentials and 401'd silently (dev-verified
            // via SSM: verify 200 → trust POST 401, Mongo 0 trusted rows).
            // Still deliberately non-blocking: a failure must never block
            // sign-in — the user just isn't remembered on this device.
            if (trustDevice) {
                await trustIssued();
            }
            if (warning) toast.warning(RECOVERY_CODE_USED_MESSAGE);
            navigate('/dashboard');
        } catch (err) {
            const parsed = parseMfaError(err);
            // The budget is per-challenge now, so "sign in again" is honest
            // advice rather than the loop it used to be: mfa_attempts lived on
            // the user document and never reset, so five wrong codes locked the
            // account out of the product permanently (#594 D2).
            if (EXPIRED_SESSION_CODES.has(parsed.code)) {
                setMfaStep(null);
                setMfaToken(null);
                setMfaError(null);
                setMfaRecoveryMode(false);
                toast.error(parsed.message);
            } else {
                setMfaError(parsed.message);
                setMfaCode('');
            }
        } finally {
            setMfaVerifying(false);
        }
    };

    const handleMfaEnrollmentComplete = async (result) => {
        // #594: /auth/mfa/enable now returns the session WITH the recovery
        // codes, so enrollment signs the user in. This used to send them to a
        // second code prompt holding the code /enable had just consumed, which
        // /verify's replay guard then rejected as "That code didn't work" —
        // deterministically, on every user's first sign-in. One production user
        // escaped that loop by burning a recovery code 140 ms later.
        //
        // The "Trust this device for 7 days" checkbox (#564) deliberately stays
        // on the returning-user code card and is NOT offered here: that screen
        // already carries a mandatory save-gate, the user is at peak cognitive
        // load having just been handed ten codes to store, and the moment a
        // factor is created is the worst moment to offer seven days of skipping
        // it. They meet the option at their next sign-in.
        setMfaError(null);
        setMfaCode('');
        if (result?.access_token) {
            adoptSession(result);
            navigate('/dashboard');
            return;
        }
        // No session in the response: the enable ran against a full session
        // (Settings → Security mounts the same component). Nothing to adopt.
        setMfaStep(null);
        setMfaToken(null);
    };

    const resetMfa = () => {
        setMfaStep(null);
        setMfaToken(null);
        setMfaError(null);
        setMfaCode('');
        setMfaRecoveryMode(false);
        setTrustDevice(false);
    };

    // Issue #564: opt in to device trust AFTER a successful MFA verification.
    // Sends the fresh Bearer token (getAuthHeader) because it runs right after
    // adoptSession — the session cookie does not exist on the MFA path (the
    // /mfa/verify response is a plain JSON body with no Set-Cookie), so a
    // Bearer-less request would 401. In the legacy cross-origin S3/CloudFront
    // mode withCredentials:true plus the Bearer header both ride along; on the
    // same-origin nginx path only the header matters.
    // Deliberately non-blocking: if issuance fails, the user still signs in —
    // they simply are not remembered on this device.
    const trustIssued = async () => {
        try {
            await axios.post(
                `${API_BASE}/auth/trusted-devices`,
                {},
                { withCredentials: true, headers: getAuthHeader() },
            );
        } catch {
            toast.message('Signed in — this device was not remembered. You can trust it later in Settings → Security.');
        }
    };

    const handleResendVerification = async () => {
        setResending(true);
        try {
            await axios.post(`${API_BASE}/auth/resend-verification`, { email });
            toast.success('Verification email sent! Check your inbox.');
        } catch {
            toast.error('Could not resend verification email. Please try again later.');
        } finally {
            setResending(false);
        }
    };

    // Issue #557: the MFA challenge screens. The state machine below already
    // existed but nothing rendered it — a challenged user saw the credentials
    // form redrawn with no error and no way forward. Both screens reuse the
    // login page's shell so the challenge reads as the same app.
    if (mfaStep === 'enroll') {
        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#FAFAF5' }}>
                <div className="w-full max-w-md">
                    <div className="text-center mb-8 animate-fade-in">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-stone-900 rounded-2xl shadow-lg mb-4">
                            <Sparkles className="w-7 h-7 text-white" />
                        </div>
                        <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                            Nurture <span className="text-stone-500">CRM</span>
                        </h1>
                        <p className="text-stone-500 mt-2 text-sm">AI-powered relationship intelligence</p>
                    </div>

                    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 animate-fade-in stagger-1">
                        {mfaError && (
                            <p data-testid="mfa-challenge-error" className="text-sm text-red-600 text-center mb-4">{mfaError}</p>
                        )}
                        <MfaEnrollment
                            api={mfaApi}
                            headers={{ Authorization: `Bearer ${mfaToken}` }}
                            onComplete={handleMfaEnrollmentComplete}
                        />
                        <button type="button" onClick={resetMfa}
                            data-testid="mfa-back-btn"
                            className="mt-3 w-full text-sm text-stone-500 hover:text-stone-700">
                            Back to sign-in
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (mfaStep === 'code') {
        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#FAFAF5' }}>
                <div className="w-full max-w-md">
                    <div className="text-center mb-8 animate-fade-in">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-stone-900 rounded-2xl shadow-lg mb-4">
                            <Sparkles className="w-7 h-7 text-white" />
                        </div>
                        <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                            Nurture <span className="text-stone-500">CRM</span>
                        </h1>
                        <p className="text-stone-500 mt-2 text-sm">AI-powered relationship intelligence</p>
                    </div>

                    <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 animate-fade-in stagger-1">
                        <h2 className="text-xl font-semibold text-stone-900 mb-1" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                            Two-step verification
                        </h2>
                        <p className="text-sm text-stone-500 mb-6">
                            {mfaRecoveryMode
                                ? 'Enter one of your recovery codes.'
                                : mfaEmail
                                    ? `Enter the 6-digit code from your authenticator app for ${mfaEmail}.`
                                    : 'Enter the 6-digit code from your authenticator app.'}
                        </p>
                        <MfaCodeInput
                            value={mfaCode}
                            onChange={setMfaCode}
                            onSubmit={handleMfaVerify}
                            error={mfaError}
                            disabled={mfaVerifying}
                            recoveryMode={mfaRecoveryMode}
                            onToggleRecovery={setMfaRecoveryMode}
                        />
                        {/* Recovery codes do not auto-submit (only the 6-digit
                            input does), so the card needs an explicit Verify.
                            In TOTP mode the button is hidden — MfaCodeInput
                            auto-submits on the 6th digit, and an extra Verify
                            click here could submit a stale or partial code and
                            burn one of the five server-side attempts. */}
                        <div className="mt-4 space-y-3">
                            {!mfaRecoveryMode ? null : (
                            <button
                                type="button"
                                onClick={() => handleMfaVerify()}
                                disabled={mfaVerifying || !mfaCode.trim()}
                                data-testid="mfa-verify-btn"
                                className="w-full py-2.5 bg-stone-900 text-white font-medium rounded-lg hover:bg-stone-800 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {mfaVerifying ? 'Verifying...' : 'Verify'}
                            </button>
                            )}
                            {/* Issue #564: opt-in only, unchecked by default.
                                Native input matching the security settings
                                page's checkbox styling (the security pages do
                                not use the shadcn/Radix primitive). */}
                            <label className="flex items-center gap-2 cursor-pointer" data-testid="trust-device-label">
                                <input
                                    type="checkbox"
                                    checked={trustDevice}
                                    onChange={(e) => setTrustDevice(e.target.checked)}
                                    disabled={mfaVerifying}
                                    data-testid="trust-device-checkbox"
                                    className="w-4 h-4 rounded border-stone-300 text-stone-900 focus:ring-stone-900/20"
                                />
                                <span className="text-sm text-stone-700">
                                    Trust this device for 7 days — you can revoke it in Settings → Security.
                                </span>
                            </label>
                            <button type="button" onClick={resetMfa}
                                data-testid="mfa-back-btn"
                                className="w-full text-sm text-stone-500 hover:text-stone-700">
                                Back to sign-in
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#FAFAF5' }}>
            <div className="w-full max-w-md">
                <div className="text-center mb-8 animate-fade-in">
                    <div className="inline-flex items-center justify-center w-14 h-14 bg-stone-900 rounded-2xl shadow-lg mb-4">
                        <Sparkles className="w-7 h-7 text-white" />
                    </div>
                    <h1 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Nurture <span className="text-stone-500">CRM</span>
                    </h1>
                    <p className="text-stone-500 mt-2 text-sm">AI-powered relationship intelligence</p>
                </div>

                {ssoAlert && (
                    <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 animate-fade-in">
                        <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="text-sm font-medium text-red-800">Sign-in failed</p>
                            <p className="text-sm text-red-600 mt-0.5">{ssoAlert}</p>
                        </div>
                        <button onClick={() => setSsoAlert(null)} className="text-red-400 hover:text-red-600 transition-colors shrink-0">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {emailNotVerified && (
                    <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 animate-fade-in">
                        <div className="flex items-start gap-3">
                            <Mail className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                            <div className="flex-1">
                                <p className="text-sm font-medium text-amber-800">Email not verified</p>
                                <p className="text-sm text-amber-700 mt-0.5">Please check your inbox for the verification link before signing in.</p>
                                <button
                                    onClick={handleResendVerification}
                                    disabled={resending}
                                    className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-amber-800 hover:text-amber-900 underline underline-offset-2"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${resending ? 'animate-spin' : ''}`} />
                                    {resending ? 'Sending...' : 'Resend verification email'}
                                </button>
                            </div>
                            <button onClick={() => setEmailNotVerified(false)} className="text-amber-400 hover:text-amber-600 transition-colors shrink-0">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                )}

                <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-8 animate-fade-in stagger-1">
                    <h2 className="text-xl font-semibold text-stone-900 mb-6" style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}>
                        Sign in to your account
                    </h2>

                    <button
                        onClick={() => { window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/sso/authorize`; }}
                        className="w-full flex items-center justify-center gap-3 px-4 py-2.5 border border-stone-200 rounded-lg text-stone-700 font-medium hover:bg-stone-50 transition-colors mb-6"
                    >
                        <LogIn className="w-5 h-5 text-stone-500" />
                        Sign in with Multiplai
                    </button>

                    <div className="relative mb-6">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-stone-200" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                            <span className="bg-white px-3 text-stone-400">or sign in with email</span>
                        </div>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Email address</label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                                <input
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    placeholder="you@company.com"
                                    data-testid="login-email-input"
                                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400 transition-all"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-xs font-semibold text-stone-500 uppercase tracking-wider mb-1.5">Password</label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                                <input
                                    type={showPw ? 'text' : 'password'}
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    data-testid="login-password-input"
                                    className="w-full pl-10 pr-10 py-2.5 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-400 transition-all"
                                />
                                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600">
                                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-btn"
                            className="w-full py-2.5 bg-stone-900 text-white font-medium rounded-lg hover:bg-stone-800 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                        >
                            {loading ? 'Signing in...' : 'Sign in'}
                        </button>
                    </form>

                    <p className="text-center text-sm mt-3">
                        <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-stone-500 hover:text-stone-700 hover:underline">
                            Forgot password?
                        </Link>
                    </p>

                    <p className="text-center text-sm text-stone-500 mt-6">
                        Don't have an account?{' '}
                        <Link to="/register" className="text-stone-900 hover:text-stone-700 font-medium">Sign up</Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
